import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from '../../../../styles/theme';
import ListingImageLightbox from './ListingImageLightbox';

const SWIPE_PX = 40;

const arrowSx = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: 6,
  transform: 'translateY(-50%)',
  zIndex: 1,
  width: 28,
  height: 28,
  color: colors.textPrimary,
  bgcolor: 'rgba(0,0,0,0.45)',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)', color: colors.textPrimary },
  '& .MuiSvgIcon-root': { fontSize: 20 },
});

/**
 * Galería de solo lectura: una foto a la vez, flechas, puntos y ampliar.
 */
export default function ListingImageCarousel({ images = [] }) {
  const list = Array.isArray(images) ? images.filter((image) => image?.url) : [];
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const swipeStartX = useRef(null);

  useEffect(() => {
    setIndex((prev) => (list.length === 0 ? 0 : Math.min(prev, list.length - 1)));
  }, [list.length]);

  if (list.length === 0) return null;

  const current = list[index] || list[0];
  const canCycle = list.length > 1;

  const go = (delta, event) => {
    event?.stopPropagation();
    if (!canCycle) return;
    setIndex((prev) => (prev + delta + list.length) % list.length);
  };

  const handleSwipeStart = (event) => {
    swipeStartX.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleSwipeEnd = (event) => {
    if (swipeStartX.current == null || !canCycle) return;
    const endX = event.changedTouches?.[0]?.clientX;
    const dx = endX - swipeStartX.current;
    swipeStartX.current = null;
    if (Math.abs(dx) < SWIPE_PX) return;
    go(dx < 0 ? 1 : -1);
  };

  return (
    <>
      <Box
        onClick={() => setLightboxOpen(true)}
        onTouchStart={handleSwipeStart}
        onTouchEnd={handleSwipeEnd}
        sx={{
          position: 'relative',
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.08)',
          aspectRatio: '16 / 9',
          bgcolor: 'rgba(255,255,255,0.04)',
          cursor: 'zoom-in',
          userSelect: 'none',
        }}
      >
        <img
          src={current.url}
          alt={`foto ${index + 1} de ${list.length}`}
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />

        {canCycle ? (
          <>
            <IconButton
              aria-label="Foto anterior"
              onClick={(event) => go(-1, event)}
              sx={arrowSx('left')}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              aria-label="Foto siguiente"
              onClick={(event) => go(1, event)}
              sx={arrowSx('right')}
            >
              <ChevronRightIcon />
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 8,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0.75,
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              {list.map((image, dotIndex) => {
                const active = dotIndex === index;
                return (
                  <Box
                    key={image.id || image.url || dotIndex}
                    component="button"
                    type="button"
                    aria-label={`Foto ${dotIndex + 1} de ${list.length}`}
                    aria-current={active ? 'true' : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      setIndex(dotIndex);
                    }}
                    sx={{
                      appearance: 'none',
                      pointerEvents: 'auto',
                      border: 0,
                      p: 0,
                      width: active ? 8 : 6,
                      height: active ? 8 : 6,
                      borderRadius: '50%',
                      bgcolor: active ? colors.textPrimary : 'rgba(255,255,255,0.4)',
                      cursor: 'pointer',
                    }}
                  />
                );
              })}
            </Box>
          </>
        ) : null}
      </Box>

      {lightboxOpen ? (
        <ListingImageLightbox
          images={list}
          index={index}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setIndex}
        />
      ) : null}
    </>
  );
}
